import React, { useEffect, useMemo, useState } from "react";
import { Download, Trash2, ChevronDown, ChevronUp, Pause, Play, X, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, API } from "@/lib/api";

const WEEKDAYS_SHORT = { 1: "Mo", 2: "Di", 3: "Mi", 4: "Do", 5: "Fr", 6: "Sa", 7: "So" };

const STATUS_TABS = [
  { key: "all", label: "Alle" },
  { key: "active", label: "Aktiv" },
  { key: "pending", label: "Unbestätigt" },
  { key: "paused", label: "Pausiert" },
  { key: "unsubscribed", label: "Abgemeldet" },
  { key: "deleted_request", label: "Löschwunsch" },
];

function formatDate(iso) { try { return new Date(iso).toLocaleString("de-DE"); } catch { return ""; } }
function formatDay(iso) { try { return new Date(iso).toLocaleDateString("de-DE"); } catch { return ""; } }

function StatusPill({ s }) {
  const map = {
    active: ["bg-[hsl(var(--brand-soft))]", "text-[hsl(var(--brand-green-dark))]"],
    pending: ["bg-neutral-100", "text-neutral-700"],
    paused: ["bg-amber-50", "text-amber-800"],
    unsubscribed: ["bg-red-50", "text-red-800"],
    deleted_request: ["bg-red-100", "text-red-800"],
  };
  const [bg, fg] = map[s] || map.pending;
  const label = { active: "Aktiv", pending: "Unbestätigt", paused: "Pausiert", unsubscribed: "Abgemeldet", deleted_request: "Löschwunsch" }[s] || s;
  return <span className={`inline-block px-2.5 py-1 text-[11px] uppercase tracking-[0.1em] font-medium ${bg} ${fg}`}>{label}</span>;
}

function LogsModal({ subId, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/admin/commuter/${subId}/logs`).then((r) => setData(r.data)).catch(() => setData({ emails: [], routes: [] }));
  }, [subId]);
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4" onClick={onClose} data-testid="commuter-logs-modal">
      <div className="bg-white max-w-3xl w-full max-h-[80vh] overflow-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-lg">Versand- & Routen-Protokoll</h3>
          <button onClick={onClose} className="text-neutral-500 hover:text-neutral-900"><X className="h-5 w-5" /></button>
        </div>
        {!data ? (
          <div className="text-neutral-500 text-sm">Lädt…</div>
        ) : (
          <div className="space-y-6 text-sm">
            <div>
              <h4 className="text-xs uppercase tracking-[0.14em] text-neutral-500 mb-2">Versendete E-Mails ({data.emails.length})</h4>
              {data.emails.length === 0 ? <p className="text-neutral-500">Noch keine Mails versendet.</p> : (
                <ul className="border border-brand-line divide-y divide-neutral-100">
                  {data.emails.map((e) => (
                    <li key={e.id} className="px-3 py-2 flex flex-wrap items-center justify-between gap-2">
                      <span>{formatDate(e.sent_at)}</span>
                      <span className="text-neutral-600">{e.fahrzeit_min != null ? `${e.fahrzeit_min} Min · ` : ""}{e.route_status}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <h4 className="text-xs uppercase tracking-[0.14em] text-neutral-500 mb-2">Berechnete Routen ({data.routes.length})</h4>
              {data.routes.length === 0 ? <p className="text-neutral-500">Noch keine Routen berechnet.</p> : (
                <ul className="border border-brand-line divide-y divide-neutral-100">
                  {data.routes.map((r) => (
                    <li key={r.id} className="px-3 py-2 text-xs">
                      <div className="flex flex-wrap justify-between gap-2">
                        <span>{formatDate(r.computed_at)}</span>
                        <span className="text-neutral-600">{Math.round(r.duration_s / 60)} Min · {(r.distance_m / 1000).toFixed(1)} km · {r.severity}</span>
                      </div>
                      {r.summary && <div className="text-neutral-500 mt-0.5">{r.summary}</div>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminCommuter() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [logsForId, setLogsForId] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/admin/commuter", { params: { status: tab, limit: 1000 } });
      setItems(r.data.items || []);
      setCounts(r.data.counts || {});
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setLoading(false);
  }
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tab]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return items;
    return items.filter((i) =>
      [i.email, i.first_name, i.last_name, i.start_address, i.destination_address]
        .filter(Boolean).join(" ").toLowerCase().includes(s),
    );
  }, [items, search]);

  async function patch(id, body) {
    try {
      await api.patch(`/admin/commuter/${id}`, body);
      toast.success("Aktualisiert.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }
  async function del(id) {
    if (!window.confirm("Diesen Eintrag und alle zugehörigen Logs unwiderruflich löschen?")) return;
    try {
      await api.delete(`/admin/commuter/${id}`);
      toast.success("Gelöscht.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  async function testSend(id, email) {
    if (!window.confirm(`Test-Routen-Mail jetzt an ${email} versenden?`)) return;
    try {
      const tid = toast.loading("Versende Test-Mail…");
      await api.post(`/admin/commuter/${id}/test-send`);
      toast.success(`Test-Mail an ${email} versendet.`, { id: tid });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  async function exportCsv() {
    const token = localStorage.getItem("admin_token");
    const res = await fetch(`${API}/admin/commuter/export.csv`, {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) { toast.error("Export fehlgeschlagen"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "berufsverkehr-ticker.csv"; a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div data-testid="admin-commuter">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-semibold text-3xl mb-1">Berufsverkehr-Ticker</h1>
          <p className="text-sm text-neutral-500">Anmeldungen, Status, Versand-Logs und CSV-Export</p>
        </div>
        <button
          onClick={exportCsv}
          data-testid="commuter-export"
          className="inline-flex items-center gap-2 border border-brand-line px-4 py-2.5 text-sm hover:bg-neutral-50"
        >
          <Download className="h-4 w-4" /> CSV-Export
        </button>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-4">
        {STATUS_TABS.map((t) => {
          const n = t.key === "all"
            ? Object.values(counts).reduce((a, b) => a + b, 0)
            : (counts[t.key] || 0);
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`commuter-tab-${t.key}`}
              className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-medium border transition-colors ${
                active
                  ? "bg-[hsl(var(--brand-green-dark))] text-white border-[hsl(var(--brand-green-dark))]"
                  : "bg-white text-neutral-700 border-brand-line hover:bg-neutral-50"
              }`}
            >
              {t.label} <span className="opacity-70">({n})</span>
            </button>
          );
        })}
      </div>

      <div className="mb-4">
        <input
          type="text"
          placeholder="Suchen nach Name, E-Mail, Adresse…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="commuter-search"
          className="w-full sm:w-96 bg-white border border-brand-line px-3.5 py-2.5 text-sm focus:outline-none focus:border-[hsl(var(--brand-green-dark))]"
        />
      </div>

      {loading ? (
        <div className="py-16 text-center text-neutral-500"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-brand-line p-10 text-center text-neutral-500" data-testid="commuter-empty">
          Keine Anmeldungen vorhanden.
        </div>
      ) : (
        <div className="border border-brand-line bg-white overflow-x-auto" data-testid="commuter-list">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-brand-line text-left text-xs uppercase tracking-[0.1em] text-neutral-500">
                <th className="px-3 py-2.5"></th>
                <th className="px-3 py-2.5">E-Mail / Name</th>
                <th className="px-3 py-2.5">Strecke</th>
                <th className="px-3 py-2.5">Abfahrt</th>
                <th className="px-3 py-2.5">Tage</th>
                <th className="px-3 py-2.5">Status</th>
                <th className="px-3 py-2.5">Letzte Mail</th>
                <th className="px-3 py-2.5 text-right">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((i) => {
                const expanded = expandedId === i.id;
                return (
                  <React.Fragment key={i.id}>
                    <tr className="border-b border-neutral-100 align-top hover:bg-neutral-50">
                      <td className="px-3 py-3">
                        <button
                          onClick={() => setExpandedId(expanded ? null : i.id)}
                          data-testid={`commuter-row-expand-${i.email}`}
                          className="text-neutral-400 hover:text-neutral-900"
                        >
                          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </button>
                      </td>
                      <td className="px-3 py-3 max-w-[220px]">
                        <div className="font-medium text-neutral-900 truncate">{i.email}</div>
                        <div className="text-xs text-neutral-500">{i.first_name} {i.last_name}</div>
                      </td>
                      <td className="px-3 py-3 max-w-[280px]">
                        <div className="text-xs text-neutral-600 truncate"><strong className="text-neutral-900">Start:</strong> {i.start_address}</div>
                        <div className="text-xs text-neutral-600 truncate"><strong className="text-neutral-900">Ziel:</strong> {i.destination_address}</div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">{i.departure_time}</td>
                      <td className="px-3 py-3 text-xs">{(i.weekdays || []).map((d) => WEEKDAYS_SHORT[d]).join(" · ")}</td>
                      <td className="px-3 py-3"><StatusPill s={i.status} /></td>
                      <td className="px-3 py-3 text-xs text-neutral-600 whitespace-nowrap">{i.last_sent_at ? formatDay(i.last_sent_at) : "—"}</td>
                      <td className="px-3 py-3">
                        <div className="flex items-center justify-end gap-1.5">
                          {i.status === "active" && (
                            <button onClick={() => patch(i.id, { status: "paused" })} data-testid={`commuter-pause-${i.email}`} title="Pausieren" className="p-1.5 text-neutral-500 hover:text-amber-800"><Pause className="h-4 w-4" /></button>
                          )}
                          {(i.status === "paused" || i.status === "pending") && (
                            <button onClick={() => patch(i.id, { status: "active" })} data-testid={`commuter-resume-${i.email}`} title="Aktivieren" className="p-1.5 text-neutral-500 hover:text-[hsl(var(--brand-green-dark))]"><Play className="h-4 w-4" /></button>
                          )}
                          <button onClick={() => setLogsForId(i.id)} data-testid={`commuter-logs-${i.email}`} title="Logs" className="px-2 py-1 text-xs border border-brand-line hover:bg-neutral-50">Logs</button>
                          {(i.status === "active" || i.status === "paused") && (
                            <button onClick={() => testSend(i.id, i.email)} data-testid={`commuter-test-${i.email}`} title="Test-Mail jetzt senden" className="p-1.5 text-neutral-500 hover:text-[hsl(var(--brand-green-dark))]"><Send className="h-4 w-4" /></button>
                          )}
                          <button onClick={() => del(i.id)} data-testid={`commuter-delete-${i.email}`} title="Löschen" className="p-1.5 text-neutral-500 hover:text-red-700"><Trash2 className="h-4 w-4" /></button>
                        </div>
                      </td>
                    </tr>
                    {expanded && (
                      <tr className="bg-neutral-50">
                        <td></td>
                        <td colSpan={7} className="px-3 py-3 text-xs text-neutral-600">
                          <div className="grid sm:grid-cols-3 gap-4">
                            <div><div className="uppercase tracking-[0.1em] text-[10px] text-neutral-500 mb-1">Angemeldet</div>{formatDate(i.created_at)}</div>
                            <div><div className="uppercase tracking-[0.1em] text-[10px] text-neutral-500 mb-1">Bestätigt</div>{i.confirmed_at ? formatDate(i.confirmed_at) : "—"}</div>
                            <div><div className="uppercase tracking-[0.1em] text-[10px] text-neutral-500 mb-1">Letzter Status</div>{i.last_route_status || "—"}</div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {logsForId && <LogsModal subId={logsForId} onClose={() => setLogsForId(null)} />}
    </div>
  );
}
